// Recent durability failures from the stand-in storage's persistence wrapper.
//
// When the Lockdown stand-in cannot make a write durable (a native file write
// fails, a rollback fails), the error the CALLER sees is Dexie's generic
// `AbortError: Transaction aborted` — the underlying cause used to go only to
// console.error, disconnected from any Sentry event (S-PROCESS-FRONTEND-8N
// told us a persist failed but not why). The wrapper records causes here so
// error captures can attach them.
//
// A tiny bounded ring, memory-only, observational: nothing branches on it.

export type DurabilityFailure = {
  /** The persist file (or "abort"/"rollback" for wrapper-stage failures). */
  file: string;
  error: unknown;
  at: number;
};

const MAX_ENTRIES = 5;
const recent: DurabilityFailure[] = [];

export function recordDurabilityFailure(file: string, error: unknown): void {
  if (recent.length === MAX_ENTRIES) {
    recent.shift();
  }

  recent.push({ file, error, at: Date.now() });
}

/**
 * Failures recent enough to plausibly explain an error being captured right
 * now, serialized for a Sentry `extra`. Newest last.
 */
export function recentDurabilityFailures(
  maxAgeMs = 30_000,
): Array<{ file: string; error: string; agoMs: number }> {
  const now = Date.now();
  return recent
    .filter((f) => now - f.at <= maxAgeMs)
    .map((f) => ({ file: f.file, error: String(f.error), agoMs: now - f.at }));
}

/** Test-only: reset the ring between cases. */
export function clearDurabilityFailuresForTests(): void {
  recent.length = 0;
}
